// CourierRequestForm.js (modified to handle dashboard users)
import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api'; // Use the central api instance
import { useJsApiLoader } from '@react-google-maps/api';
import './CourierRequestForm.css';

// Phone number formatting function
const formatPhoneNumber = (value) => {
  // Remove all non-digit characters
  let phoneNumber = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  phoneNumber = phoneNumber.substring(0, 10);
  
  // Format the phone number as (XXX) XXX-XXXX
  if (phoneNumber.length > 0) {
    if (phoneNumber.length <= 3) {
      phoneNumber = `(${phoneNumber}`;
    } else if (phoneNumber.length <= 6) {
      phoneNumber = `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(3)}`;
    } else {
      phoneNumber = `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(3, 6)}-${phoneNumber.substring(6, 10)}`;
    }
  }
  
  return phoneNumber;
};

const CourierRequestForm = ({ isFromDashboard = false }) => {
  const [formData, setFormData] = useState({
    dispensaryName: '',
    address: '',
    contactPhone: '',
    orderNumber: '',
    customerName: '',
    customerPhone: '',
    deliveryAddress: '',
    orderTotal: '',
    notes: '',
    pickupTimeOption: 'asap',
    requestedPickupTime: '',
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // References for the address input fields
  const dispensaryAddressRef = useRef(null);
  const deliveryAddressRef = useRef(null);

  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  // Initialize autocomplete after Google Maps API is loaded
  useEffect(() => {
    if (!isLoaded || loadError) return;

    // Initialize autocomplete for dispensary address
    if (dispensaryAddressRef.current) {
      const autocompleteDispensary = new window.google.maps.places.Autocomplete(
        dispensaryAddressRef.current,
        { types: ['address'], componentRestrictions: { country: 'us' } }
      );

      autocompleteDispensary.addListener('place_changed', () => {
        const place = autocompleteDispensary.getPlace();
        if (place?.formatted_address) {
          setFormData({
            ...formData,
            address: place.formatted_address
          });
        }
      });
    }

    // Initialize autocomplete for delivery address
    if (deliveryAddressRef.current) {
      const autocompleteDelivery = new window.google.maps.places.Autocomplete(
        deliveryAddressRef.current,
        { types: ['address'], componentRestrictions: { country: 'us' } }
      );

      autocompleteDelivery.addListener('place_changed', () => {
        const place = autocompleteDelivery.getPlace();
        if (place?.formatted_address) {
          setFormData({
            ...formData,
            deliveryAddress: place.formatted_address
          });
        }
      });
    }
  }, [isLoaded, loadError, formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Apply phone number formatting for phone fields
    if (name === 'contactPhone' || name === 'customerPhone') {
      setFormData({
        ...formData,
        [name]: formatPhoneNumber(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const calculateCourierPayment = () => {
    const total = parseFloat(formData.orderTotal) || 0;
    
    // Dashboard users (courier partners) pay 15% less
    // Regular users pay 22% less
    if (isFromDashboard) {
      return total * 0.85; // 15% discount for courier partners
    } else {
      return total * 0.8; // 20% discount for regular users
    }
  };

  const validateForm = () => {
    // Basic validation - you could expand this as needed
    if (!formData.dispensaryName) return false;
    if (!formData.address) return false;
    if (!formData.contactPhone) return false;
    if (!formData.orderNumber) return false;
    if (!formData.customerName) return false;
    if (!formData.customerPhone) return false;
    if (!formData.deliveryAddress) return false;
    if (!formData.orderTotal) return false;
    
    // Additional validation for scheduled pickup
    if (formData.pickupTimeOption === 'scheduled' && !formData.requestedPickupTime) {
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    // Reset error state
    setError(null);
    
    // Validate form
    if (!validateForm()) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      // Create a copy of formData with empty products array to satisfy API requirements
      const dataToSubmit = {
        ...formData,
        products: [], // Include an empty products array if your API still expects this field
        isFromDashboard: isFromDashboard // Add flag to indicate if it's from dashboard
      };
      
      // Call API to create courier request using the api instance
      const response = await api.post('/courier-requests', dataToSubmit);
      
      console.log('Submitted order request:', dataToSubmit);
      console.log('Response:', response.data);
      setSubmitted(true);
      setLoading(false);
    } catch (error) {
      console.error('Error submitting courier request:', error);
      setError(error.response?.data?.message || 'Failed to submit request. Please try again.');
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      dispensaryName: '',
      address: '',
      contactPhone: '',
      orderNumber: '',
      customerName: '',
      customerPhone: '',
      deliveryAddress: '',
      orderTotal: '',
      notes: '',
      pickupTimeOption: 'asap',
      requestedPickupTime: '',
    });
    setSubmitted(false);
    setError(null);
  };

  // Show loading state while Google Maps API is loading
  if (loadError) return <div className="error-message">Error loading Google Maps API</div>;
  if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className="form-container">
      <h2 className="form-title">Courier Pickup Request</h2>
      
      {submitted ? (
        <div className="success-message">
          <h3 className="success-title">Pickup Request Submitted!</h3>
          <p className="success-text">A courier will be assigned to your order shortly.</p>
          <button 
            onClick={resetForm}
            className="button button-primary"
            style={{ marginTop: '1rem' }}
          >
            Create Another Request
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="form-space-y">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <div className="form-section">
            <h3 className="form-section-title">Dispensary Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Dispensary Name*</label>
                <input
                  type="text"
                  name="dispensaryName"
                  value={formData.dispensaryName}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Address*</label>
                <input
                  type="text"
                  name="address"
                  ref={dispensaryAddressRef}
                  value={formData.address}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Start typing for suggestions"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Contact Phone*</label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="(123) 456-7890"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Order Number*</label>
                <input
                  type="text"
                  name="orderNumber"
                  value={formData.orderNumber}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Delivery Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Customer Name*</label>
                <input
                  type="text"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Customer Phone*</label>
                <input
                  type="tel"
                  name="customerPhone"
                  value={formData.customerPhone}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="(123) 456-7890"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Delivery Address*</label>
                <input
                  type="text"
                  name="deliveryAddress"
                  ref={deliveryAddressRef}
                  value={formData.deliveryAddress}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Start typing for suggestions"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Pickup Time</label>
                <div className="radio-group">
                  <div className="radio-option">
                    <input
                      id="pickup-asap"
                      type="radio"
                      name="pickupTimeOption"
                      value="asap"
                      checked={formData.pickupTimeOption === 'asap'}
                      onChange={handleInputChange}
                      className="radio-input"
                    />
                    <label htmlFor="pickup-asap" className="radio-label">
                      ASAP
                    </label>
                  </div>
                  <div className="radio-option">
                    <input
                      id="pickup-scheduled"
                      type="radio"
                      name="pickupTimeOption"
                      value="scheduled"
                      checked={formData.pickupTimeOption === 'scheduled'}
                      onChange={handleInputChange}
                      className="radio-input"
                    />
                    <label htmlFor="pickup-scheduled" className="radio-label">
                      Specific Time
                    </label>
                  </div>
                  {formData.pickupTimeOption === 'scheduled' && (
                    <input
                      type="datetime-local"
                      name="requestedPickupTime"
                      value={formData.requestedPickupTime}
                      onChange={handleInputChange}
                      className="form-input datetime-input"
                      required={formData.pickupTimeOption === 'scheduled'}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <label className="form-label">Order Total Amount (before taxes)*</label>
            <div className="currency-input">
              <span className="currency-symbol">$</span>
              <input
                type="number"
                name="orderTotal"
                min="0"
                step="0.01"
                value={formData.orderTotal}
                onChange={handleInputChange}
                className="form-input"
                placeholder="0.00"
                required
              />
            </div>
          </div>
          
          <div className="total-section">
            <div className="total-row">
              <span className="total-label">
                {isFromDashboard ? 'Partner' : 'Courier'} Payment (before taxes):
              </span>
              <span className="total-value">${calculateCourierPayment().toFixed(2)}</span>
            </div>
            {isFromDashboard && (
              <div className="total-row" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                <span style={{ color: '#f0fdf4' }}>Partner Commission Rate: 15%</span>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Request Courier Pickup'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default CourierRequestForm;